import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useQueries } from '@tanstack/react-query';
import { createDataLoader } from '../utilities/create-data-loader';
import { createApiClient } from '../api/client';
import { getAssetDisplayParts } from '../utilities/asset-id-utils';
import { regionState } from '../store/common-atoms';
import { useIdResolver, useIdResolverPrefetch } from './useIdResolver';
import { ResolverQueryClientContext, registerResolverClearCallback } from './useResolverQueryClient';

const NS = 'asset-display';
const ENTITY_PATH = '/entity';

// One DataLoader per region. Region values are a small finite set (`us`,
// `eu`, plus `_default`), so this Map stays bounded to ~3 entries for the
// lifetime of the page and does not require eviction.
const loadersByRegion = new Map();

const fallbackEntry = id => {
  const parts = getAssetDisplayParts(id);
  return { type_id: parts.type, customer_id: parts.customer, name: parts.name };
};

const makeBatchFetch = region => async ids => {
  const { data } = await createApiClient(region).post(ENTITY_PATH, {
    filters: { _id: ids },
    with_links: false,
    limit: ids.length,
    show_deleted: true
  });
  const entities = data?.response?.entity || [];
  const byId = new Map();
  entities.forEach(e => {
    if (!e?._id) return;
    const parts = getAssetDisplayParts(e._id);
    byId.set(e._id, {
      type_id: e.type_id || parts.type,
      customer_id: e.customer_id || parts.customer,
      name: e.name || parts.name
    });
  });
  ids.forEach(id => {
    if (!byId.has(id)) byId.set(id, fallbackEntry(id));
  });
  return byId;
};

const getLoader = region => {
  const key = region || '_default';
  let loader = loadersByRegion.get(key);
  if (!loader) {
    loader = createDataLoader({ batchFetch: makeBatchFetch(region) });
    loadersByRegion.set(key, loader);
  }
  return loader;
};

export const clearAssetDisplayLoaders = () => loadersByRegion.clear();

registerResolverClearCallback(clearAssetDisplayLoaders);

export const useAssetDisplay = id => {
  const region = useAtomValue(regionState);
  const loader = useMemo(() => getLoader(region), [region]);
  return useIdResolver({
    ns: `${NS}:${region || '_default'}`,
    id,
    loader,
    select: data => data ?? fallbackEntry(id)
  });
};

export const useAssetDisplayPrefetch = () => {
  const region = useAtomValue(regionState);
  const loader = useMemo(() => getLoader(region), [region]);
  return useIdResolverPrefetch({ ns: `${NS}:${region || '_default'}`, loader });
};

const STALE_TIME = 15 * 60 * 1000;
const GC_TIME = 60 * 60 * 1000;

// Bulk-read variant. All `useAssetDisplays(ids)` calls within the same
// commit feed their queryFns into the DataLoader microtask flush -> one
// POST /entity per region. Returns a Map<id, { type_id, customer_id, name }>
// that re-renders the consumer as the cache fills.
export const useAssetDisplays = ids => {
  const region = useAtomValue(regionState);
  const loader = useMemo(() => getLoader(region), [region]);
  const safeIds = useMemo(() => Array.from(new Set((ids || []).filter(Boolean))), [ids]);

  const results = useQueries({
    queries: safeIds.map(id => ({
      queryKey: [`${NS}:${region || '_default'}`, id],
      queryFn: () => loader.load(id),
      staleTime: STALE_TIME,
      gcTime: GC_TIME
    })),
    context: ResolverQueryClientContext
  });

  return useMemo(() => {
    const map = new Map();
    safeIds.forEach((id, i) => {
      map.set(id, results[i]?.data || fallbackEntry(id));
    });
    return map;
  }, [safeIds, results]);
};
